import { CategoryAPI } from '@business/api/category';
import { CategoryId, CategoryResponse } from '@business/entities/category';
import { RegionId } from '@business/entities/tm';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { askChangeReason } from '@modules/common/ChangeReasonModal';
import { map, switchMap, tap } from 'rxjs/operators';

export class CategoryBloc extends BaseBloc {
  private readonly categoryRepo = new Repository<CategoryResponse>({
    getItemId: category => category.id,
  });

  readonly categories$ = this.categoryRepo.items$;
  readonly selectCategory = this.categoryRepo.selectItem;
  readonly parentCategories$ = this.categoryRepo.items$.pipe(map(categories => categories.filter(cat => !cat.parentId || cat.parentId == '0')));

  onReset() {
    this.categoryRepo.reset();
  }

  fetchCategories = (withTrashed = false, regionId?: RegionId) => {
    return CategoryAPI.fetchCategories(withTrashed, regionId).pipe(
      this.categoryRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_category_error') }),
    );
  };

  fetchCategory = (id: CategoryId) => {
    return CategoryAPI.fetchCategory(id).pipe(
      this.categoryRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_category_error') }),
    );
  };

  createCategory = (data: Partial<CategoryResponse & { canUpdateTemplate: boolean }>) => {
    return CategoryAPI.createCategory(data).pipe(
      this.categoryRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_category'),
      }),
    );
  };

  // Edits and deletes ask for the reason first (askChangeReason, kept in the Activity Log); closing that window saves
  // nothing
  updateCategory = (id: CategoryId, data: Partial<CategoryResponse & { canUpdateTemplate: boolean }>) => {
    return askChangeReason().pipe(
      switchMap(changeNote => CategoryAPI.updateCategory(id, data, changeNote)),
      this.categoryRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_category'),
      }),
    );
  };

  deleteCategory = (id: CategoryId) => {
    return askChangeReason('Delete Category', 'Do you really want to remove this category?', 'Delete').pipe(
      switchMap(changeNote => CategoryAPI.deleteCategory(id, changeNote)),
      this.categoryRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_category'),
      }),
    );
  };

  syncCategories = () => {
    return CategoryAPI.syncCategories().pipe(
      this.categoryRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        type: requestMessage('sync_category'),
      }),
    );
  };

  sortCategories = (ids: CategoryId[], parentCatId?: CategoryId) => {
    return CategoryAPI.sortCategories(ids, parentCatId).pipe(
      this.categoryRepo.ops.upsertMany(item => ({ item })),
      // handleMessage({
      //   type: requestMessage('sort_category'),
      // }),
    );
  };
}
